import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import eventBus from "../../components/eventBus";
import {
    DataGrid,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarFilterButton,
} from "@mui/x-data-grid";
import styled from "styled-components";
import Drawer from "../../components/common/drawer/drawer";
import { Typography, Button, Checkbox, Fab, IconButton, Stack } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Add } from "@mui/icons-material";
import OpaqueLoading from "../../components/opaqueLoading/opaqueLoading";
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import NotificationEmailModal from "../../components/modals/NotificationEmailModal";
import SessionManagementModal from "../../components/modals/SessionManagementModal";

const isMobile = window.innerWidth < 900;

const StaffEditorPageContainer = styled.div`
  height: ${isMobile ? `auto` : `calc(100vh - 105px)`};
  width: 100%;
  background-color: white;
  padding: 10px 15px;
  ${isMobile &&
    `
      position: relative;
  
      `}
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${isMobile ? "flex-start" : "space-between"};
  margin-bottom: 10px;
  flex-direction: ${isMobile ? "column" : "row"};
`;

const TableContainer = styled.div`
  height: calc(
    100vh - ${isMobile ? "56px - 20px - 82.23px" : "100px - 20px - 43.77px"}
  );
  width: 100%;
  border: solid 1px lightGrey;
  border-radius: 8px;
`;

const DataGridContainer = styled.div`
  max-width: 100%; /* Add this line to limit the width */
  margin: 0 auto; /* Center the content horizontally */
  height: calc(100% );
  width: 100%;
  border: solid 1px lightGrey;
  border-radius: 8px;
  display: ${isMobile ? 'flex' : 'grid'};

  ${isMobile &&
    `
    flex-direction: column;
  `}
`;

export default function SessionManagement() {

    const [loading, setLoading] = useState(false);
    const [accessChanges, setAccessChanges] = useState("");
    const [visitModal, setVisitModal] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [dataModal, setDataModal] = useState();
    const navigate = useNavigate();

    useEffect(() => {
        getAllSessions();
    }, []);

    const getAllSessions = async () => {
        setLoading(true);
        try {
            let results = await window.Platform.database.getAllSessions();
            if (results?.status) {
                const jsonArrayWithId = results?.data?.map((obj) => ({
                    ...obj,
                    dbId: obj.id
                }));
                setAccessChanges(jsonArrayWithId);
                setLoading(false);
            }
            else {
                setLoading(false);
                window.NotificationUtils.showError("Error Fetching Data");
            }

        } catch (error) {
            console.error("ERRORCHECK", error);
            window.NotificationUtils.showError("Error Fetching Data");
            setLoading(false);
        }
    };

    const handleDelete = async (data) => {
        const isConfirmed = window.confirm("Are you sure you want to delete this");
        if (!isConfirmed) return;

        try {
            setLoading(true);
            await window.Platform.database.deleteSession({ id: data });
            window.NotificationUtils.showSuccess("Delete successfully!");
            getAllSessions();
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    async function handleActive(newData) {
        const isConfirmed = window.confirm("Are you sure to active this?");
        if (!isConfirmed) return;
        try {
            setLoading(true);
            const payload = { id: newData.id, actionName: "active" };
            const res = await window.Platform.database.updateSessionStatus(payload);
            if (res?.status) {
                window.NotificationUtils.showError(res?.message || "Failed to update status");
            } else {
                window.NotificationUtils.showSuccess(res?.message || "Session activated");

                eventBus.emit("SESSION_UPDATED");   // Message Send to NavBar for update session drawer.js

                await getAllSessions();
            }
        } catch (err) {
            console.error(err);
            window.NotificationUtils.showError(err?.message || "Error updating status");
        } finally {
            setLoading(false);
        }
    }

    async function handleInActive(newData) {
        const isConfirmed = window.confirm("Are you sure to inactive this?");
        if (!isConfirmed) return;
        try {
            setLoading(true);
            const payload = { id: newData.id, actionName: "inactive" };
            const res = await window.Platform.database.updateSessionStatus(payload);
            if (res?.status) {
                window.NotificationUtils.showError(res?.message || "Failed to update status");
            } else {
                window.NotificationUtils.showSuccess(res?.message || "Session deactivated");
                eventBus.emit("SESSION_UPDATED");     // Message Send to NavBar for update session drawer.js
                await getAllSessions();
            }
        } catch (err) {
            console.error(err);
            window.NotificationUtils.showError(err?.message || "Error updating status");
        } finally {
            setLoading(false);
        }

    }

    const getEmailAdminColumns = () => [
        {
            field: "Name",
            headerName: "Name",
            width: 250,
            sortable: false,
            valueGetter: (params) => params.row?.sessionName || "N/A",
            renderCell: (params) => (
                <Tooltip title={params.row?.sessionName || "N/A"}>
                    <Typography variant="inherit" sx={{ color: "black", fontSize: "14px", fontWeight: "400" }}>{params.row?.sessionName || "N/A"}</Typography>
                </Tooltip>
            ),
        },
        {
            field: "StartDate1",
            headerName: "Start Date",
            width: 200,
            sortable: false,
            valueGetter: (params) => {
                const date = params.row?.startDate;
                if (!date) return "N/A";
                const localDate = new Date(date);
                return localDate.toLocaleDateString("en-GB"); // shows DD/MM/YYYY
            },
            renderCell: (params) => (
                <Tooltip title={params.value}>
                    <Typography variant="inherit" sx={{ color: "black", fontSize: "14px", fontWeight: "400" }}>
                        {params.value}
                    </Typography>
                </Tooltip>
            ),
        },
        {
            field: "EndDate1",
            headerName: "End Date",
            width: 200,
            sortable: false,
            valueGetter: (params) => {
                const date = params.row?.endDate;
                if (!date) return "N/A";
                const localDate = new Date(date);
                return localDate.toLocaleDateString("en-GB");
            },
            renderCell: (params) => (
                <Tooltip title={params.value}>
                    <Typography variant="inherit" sx={{ color: "black", fontSize: "14px", fontWeight: "400" }}>
                        {params.value}
                    </Typography>
                </Tooltip>
            ),
        },

        {
            field: "actions",
            headerName: "Actions",
            sortable: false,
            width: 360,
            renderCell: (params) => (
                <>
                    <Tooltip title="Update">
                        <IconButton onClick={() => handleEdit(params.row)}>
                            <DriveFileRenameOutlineIcon sx={{ color: "black", fontSize: "22px", fontWeight: "400" }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton onClick={() => handleDelete(params.row.id)}>
                            <DeleteIcon sx={{ color: "Red", fontSize: "22px", fontWeight: "400" }} />
                        </IconButton>
                    </Tooltip>
                    {/* <Tooltip title="Active" className="ml-2">{console.log(params?.row,"askjdbaskdbasdbkjabkasdbkasbd")}
                           <Button disabled = {params?.row?.action == "active"} onClick={() => handleActive({id :params.row.id, actionName : "active"})} variant="contained" color="primary" >Active</Button>
                    </Tooltip> */}
                    <Tooltip className="ml-2">
                        <Button
                            disabled={params?.row?.action === "active"}
                            onClick={() =>
                                handleActive({ id: params.row.id, actionName: "active" })
                            }
                            variant="contained"
                            style={{
                                backgroundColor:
                                    params?.row?.action === "active" ? "#036f27ff" : "#4c778dff",
                                color: "white",
                                cursor: params?.row?.action === "active" ? "not-allowed" : "pointer",
                                width: "120px"
                            }}
                        >
                            {params?.row?.action === "active" ? "Activated" : "Active"}
                        </Button>
                    </Tooltip>

                    {params?.row?.action == "active" && <Tooltip title="Inactive" className="ml-3">
                        <Button disabled={params?.row?.action == "inactive"} onClick={() => handleInActive({ id: params.row.id, actionName: "inactive" })} variant="contained" color="warning"
                            style={{
                                backgroundColor:
                                    params?.row?.action === "inactive" ? "#ff0000ff" : "#ff0000ff",
                                color: "white",
                                cursor: params?.row?.action === "inactive" ? "not-allowed" : "pointer",
                                width: "120px"
                            }}
                        >Inactive</Button>
                    </Tooltip>}
                </>
            ),
        }
    ];
    function CustomToolbar() {
        return (
            <GridToolbarContainer>
                {/* <GridToolbarColumnsButton />
        <GridToolbarFilterButton /> */}
            </GridToolbarContainer>
        );
    }
    const goBack = () => {
        navigate(-1);
    }


    const renderContent = () => {
        return (
            <Box sx={{ height: "100%", width: "100%" }}>
                <DataGridContainer>
                    <DataGrid
                        className="payrollGrid"
                        density="compact"
                        rows={accessChanges || []}
                        columns={getEmailAdminColumns()}
                        sx={{ color: "black", fontSize: "14px", fontWeight: "600" }}
                        rowHeight={60}
                        disableSelectionOnClick
                        disableRowSelectionOnClick
                        autoPageSize
                        components={{
                            Toolbar: CustomToolbar,
                        }}
                        pageSizeOptions={[10]}
                    />
                </DataGridContainer>
            </Box>
        );
    };

    const addVisit = () => {
        setVisitModal(true);
    }

    const handleEdit = (data) => {
        setEditModal(true);
        setDataModal(data);
    }

    return (
        <>
            {isMobile && (
                <Drawer />
            )}
            <StaffEditorPageContainer>
                {loading && <OpaqueLoading />}
                <HeaderContainer>
                    <div>
                        <Button variant="outlined" onClick={goBack} sx={{ mb: 2 }}>
                            <ArrowBackIcon sx={{ marginRight: "2px" }} /> Back
                        </Button>
                    </div>

                    <Stack direction={"row"} spacing={2} >
                        <Button variant="contained" color="primary"
                            onClick={() => addVisit()} sx={{ mr: 1, borderRadius: '10px' }}>
                            <Add sx={{ mr: 1 }} />
                            Add New
                        </Button>
                    </Stack>

                </HeaderContainer>
                <TableContainer>{renderContent()}</TableContainer>
            </StaffEditorPageContainer>
            {
                visitModal &&
                <SessionManagementModal
                    onClose={() => setVisitModal(false)}
                    loading={loading}
                    fetchData={() => getAllSessions()}
                    status={true}
                />
            }
            {
                editModal &&
                <SessionManagementModal
                    onClose={() => setEditModal(false)}
                    loading={loading}
                    fetchData={() => getAllSessions()}
                    status={false}
                    propData={dataModal}
                />
            }

        </>
    );
}
